//Services
import { postReq } from 'src/services/http';
import { format } from 'svelte-i18n';
import { gen2Request } from 'src/services/gen_2_http_reqs';
//Stores
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import { get } from 'svelte/store';
//Types
import type { Device } from 'src/types/Device';
import type { CloudApiResponse } from 'src/types/responses/CloudApiResponse';
import type { SettingRequest } from 'src/types/requests/settings/setting';
//Helpers
import { _deepClone } from '../utilities';
import { isWallDisplay, real_id } from '../interface_helpers';
//Libraries
import { compare } from 'compare-versions';
import { deleteDevice, deleteSingleChannelInterfaceData } from '../add_delete_helpers';
//Design system components
import { toast } from '@Shelly/shelly-design-system/index';

const _format = get(format);

/**
 * Displays a success toast notification if it hasn't been shown already.
 *
 */
export const showSuccessToast = () => {
	toast.open({
		type: 'success',
		title: _format('setting_scc_saved_toast', {
			default: 'Setting saved successfully!',
		}),
	});
};

const devicesInProgress = {
	created: {},
	deleted: {},
};

/**
 * Extract Wall Display component from the device and create a new device in the cloud
 * @param {Device} device - the device to extract the component from
 * @param {string} component - the component to extract (media player or thermostat)
 * @returns {Promise<CloudApiResponse>} - the response from the cloud
 */
export const extractWallDisplayComponent = async (
	device,
	component: string,
): Promise<CloudApiResponse> => {
	const id = real_id(device.id); //device.id;
	//media player - WDID_100
	//thermostat - WDID_101
	const isMediaPlayer = /^media_player/.test(component);
	const componentID = isMediaPlayer ? '100' : '101';
	const componentType = isMediaPlayer ? 'media_player' : 'thermostat';
	const componentTitle = isMediaPlayer ? 'media player' : 'thermostat';
	const extractedID = `${id}_${componentID}`;
	const cloneDevice: any = _deepClone(device);
	if (devicesInProgress.created[extractedID]) {
		return;
	}
	devicesInProgress.created[extractedID] = true;
	const positions = Object.values(get(devices) || [])
		.filter((v: Device) => v.room_id == device.room_id)
		.map((i) => i.position);
	const position = Math.max(0, ...positions) + 1;
	delete cloneDevice.mainActionType; //remove custom card props
	delete cloneDevice.statusLabelParam; //remove custom card props
	delete cloneDevice.virtualDecimalPrecision; //remove custom card props
	const extractedDevice: Device = {
		...cloneDevice,
		id: extractedID,
		channel: parseInt(componentID),
		type: device.type,
		name: `${device.name}'s ${componentTitle}`,
		category: componentType,
		mode: component,
		image: `images/device_images/${device.type}.png`,
		// icon: 'far fa-cube',//We don't need an icon for the wall display extension devices. It looks better with device's own image
		position,
	};

	const res: CloudApiResponse = await postReq(`/interface/device/save`, {
		id: extractedID,
		data: JSON.stringify(extractedDevice),
	});
	if (res?.isok) {
		if (isMediaPlayer) {
			device.media_player_enabled = true;
			devices.update(device);
		}
	}
	devicesInProgress.created[extractedID] = false;
	return res;
};

/**
 * Disables a wall display extension component (thermostat or media player)
 *
 * This function disables the functionality of an extension without deleting the device:
 * - For thermostat (_101): Calls Thermostat.Delete to remove thermostat configuration
 * - For media player (_100): Sets media_player_enabled to false on the main device
 *
 * The device entry remains in the system but the feature is deactivated.
 *
 * @param {Device} device - The wall display extension device to disable
 * @returns {Promise<CloudApiResponse | SettingRequest | undefined>} Response from the API call
 *
 * @example
 * // Disable thermostat extension
 * const thermostatDevice = { id: 'shellywd-abc123_101', ... };
 * await disableWallDisplayExtention(thermostatDevice);
 *
 * @example
 * // Disable media player extension
 * const mediaPlayerDevice = { id: 'shellywd-abc123_100', ... };
 * await disableWallDisplayExtention(mediaPlayerDevice);
 */
export const disableWallDisplayExtention = async (device: Device) => {
	const id = device.id;
	const isThermostat = id.includes('_101');
	const isMediaPlayer = id.includes('_100');
	let res;

	if (isThermostat) {
		const resDeleteThermostat: SettingRequest = await gen2Request(device, 'Thermostat.Delete', {
			id: 0,
		});
		if (resDeleteThermostat.isok) {
			res = resDeleteThermostat;
		}
	}

	if (isMediaPlayer) {
		const mainDevice = get(devices)[real_id(id)];
		0;
		let editDevice = {
			...mainDevice,
			media_player_enabled: false,
		};

		res = await postReq('interface/device/save', {
			id: editDevice.id,
			data: JSON.stringify(editDevice),
		});
	}

	return res;
};

/**
 * Deletes a wall display extension device and removes it from the store
 *
 * This function handles deletion of:
 * - Main wall display devices (no underscore suffix)
 * - Thermostat extensions (_101 suffix)
 * - Media player extensions (_100 suffix)
 *
 * The function prevents duplicate deletions using an in-progress tracker and removes
 * the device from the local store upon successful deletion from the cloud.
 *
 * @param {Device} device - The wall display extension device to delete
 * @returns {Promise<CloudApiResponse>} Response from the deletion API call
 * @throws {Error} If the deletion fails or encounters an error
 *
 * @example
 * // Delete a thermostat extension
 * const thermostatDevice = { id: 'shellywd-abc123_101', ... };
 * await deleteWallDisplayExtention(thermostatDevice);
 *
 * @example
 * // Delete main wall display device
 * const mainDevice = { id: 'shellywd-abc123', ... };
 * await deleteWallDisplayExtention(mainDevice);
 */
export const deleteWallDisplayExtention = async (device: Device): Promise<CloudApiResponse> => {
	const id = device.id;
	const isThermostat = id.includes('_101');
	const isMediaPlayer = id.includes('_100');
	const isMainDevice = !id.includes('_');

	if (devicesInProgress.deleted[id]) {
		return;
	}

	try {
		let res;
		if (isMainDevice) {
			res = await deleteDevice(device.id);
		} else if (isThermostat || isMediaPlayer) {
			res = await deleteSingleChannelInterfaceData(device);
		}

		if (res?.isok) {
			devices.remove(id);
		} else {
			throw new Error('Failed to delete wall display extension');
		}

		return res;
	} catch (error) {
		console.error('Error deleting wall display extension:', error);
		throw error;
	}
};

/**
 * Removes all wall display components (main device and channel devices)
 *
 * This function performs a bulk deletion of all devices associated with a wall display ID,
 * including:
 * - The main device
 * - All channel devices (_1, _2, _3, etc.)
 * - Extension devices (_100, _101)
 *
 * All matching devices are deleted from the cloud and removed from the local store.
 *
 * @param {string} id - The wall display device ID to remove all components for
 * @returns {Promise<CloudApiResponse | undefined>} Response from the bulk deletion API call, or undefined if no devices found
 *
 * @example
 * // Remove all components of a wall display
 * await removeWallDisplayComponents('shellywd-abc123');
 * // This will delete: shellywd-abc123, shellywd-abc123_1, shellywd-abc123_2, shellywd-abc123_100, shellywd-abc123_101, etc.
 */
export const removeWallDisplayComponents = async (id) => {
	const wallDisplayChannels = Object.keys(get(devices)).filter((_id) => _id.includes(id));
	if (!wallDisplayChannels.length) {
		return;
	}
	try {
		const res: CloudApiResponse = await postReq(`/interface/device/bulk_delete`, {
			ids: JSON.stringify(wallDisplayChannels),
		});
		if (res?.isok) {
			wallDisplayChannels.forEach((channelId) => {
				devices.remove(channelId);
			});
		} else {
			throw new Error('Failed to delete channel devices');
		}
		return res;
	} catch (error) {
		console.error(error);
	}
};

/**
 * Updates channel devices based on status.sys.ch components
 *
 * When a wall display has multiple components in status.sys.ch (e.g., ['switch:0', 'switch:1']),
 * we automatically split it into separate channel devices:
 * - deviceId (main device) → uses first component (index 0)
 * - deviceId_1 → second component (index 1)
 * - deviceId_2 → third component (index 2), etc.
 *
 * This function handles:
 * - Creating new channel devices when components are added
 * - Updating the main device's category/mode when the first component type changes
 * - Deleting orphaned channel devices when components are removed
 *
 * Note: This does NOT affect media player (_100) or thermostat (_101) devices which are handled separately.
 *
 * @param {string} realID - The real device ID
 * @param {Device} realDevice - The main device object
 * @param {object} status - The device status containing sys.ch component array
 * @param {object} settings - The device settings
 * @param {string[]} wallDisplayChannels - Array of existing wall display channel IDs
 * @returns {Promise<void>}
 */
const updateChannelDevicesFromComponents = async (
	realID: string,
	realDevice: Device,
	status: any,
	settings: any,
	wallDisplayChannels: string[],
) => {
	if (!status?.sys?.ch) {
		return;
	}
	// Check if status.sys.ch has multiple components (more than 1)
	if (Array.isArray(status.sys.ch) && status.sys.ch.length > 1) {
		// Iterate through each component in status.sys.ch to create channel devices
		for (let i = 0; i < status.sys.ch.length; i++) {
			const component = status.sys.ch[i]; // e.g., 'switch:0', 'light:0'
			const componentType = component.split(':')[0]; // Extract type: 'switch' or 'light'
			const channelID = i === 0 ? realID : `${realID}_${i}`; // deviceId, deviceId_1, deviceId_2...

			// Update the main device (index 0) if its component type has changed
			if (i === 0) {
				// Map component type to device category and mode
				// Currently, 'switch' maps to relay, all others are mapped to their own category.
				const newCategory = componentType === 'switch' ? 'relay' : componentType;

				// Update main device if category or mode has changed
				if (realDevice.category !== newCategory || realDevice.mode !== newCategory) {
					const updatedDevice = {
						...realDevice,
						category: newCategory,
						mode: newCategory,
					};
					try {
						const res = await postReq(`/interface/device/save`, {
							id: realID,
							data: JSON.stringify(updatedDevice),
						});

						// Update store immediately for reactive UI
						if (res?.isok) {
							devices.update(updatedDevice);
						} else {
							throw new Error('Failed to update main device');
						}
					} catch (error) {
						console.error(error);
					}
				}

				continue; // Skip creating channel device for index 0
			}

			// Skip if this channel device was already created
			if (get(devices)[channelID]) {
				continue;
			}

			// Map component type to device category and mode
			const category = componentType !== 'light' ? 'relay' : componentType;

			// Prevent duplicate creation if already in progress
			if (devicesInProgress.created[channelID]) {
				continue;
			}
			devicesInProgress.created[channelID] = true;

			// Clone the main device to inherit properties (image, room, etc.)
			const cloneDevice: any = _deepClone(realDevice);

			// Push new device clone right after the main device
			const position = realDevice.position + 1;

			// Remove custom UI properties that shouldn't be inherited
			delete cloneDevice.mainActionType;
			delete cloneDevice.statusLabelParam;
			delete cloneDevice.virtualDecimalPrecision;

			// Build the channel device object with inherited and specific properties
			const channelDevice: Device = {
				...cloneDevice,
				id: channelID,
				channel: i,
				gen: realDevice.gen,
				room_id: realDevice.room_id,
				type: realDevice.type,
				name: `${realDevice.name} ${i}`, // Append channel number to name
				category,
				mode: category,
				// Always use the main device's image (custom or default)
				image: `images/device_images/${realDevice.type}.png`,
				localImage: realDevice.localImage, // Preserve localImage flag if exists
				position,
			};
			try {
				// Save the channel device to the cloud
				const res = await postReq(`/interface/device/save`, {
					id: channelID,
					data: JSON.stringify(channelDevice),
				});

				// Update stores immediately for reactive UI (no page refresh needed)
				if (res?.isok) {
					// Add device to devices store. Status/settings stay on the parent
					// real_id — the runtime store keys by real_id(), so writing a
					// channel-filtered blob here would overwrite switch:0 (FE-2385).
					devices.update(channelDevice);

					toast.open({
						type: 'success',
						title: _format('success_global', {
							default: 'Success',
						}),
					});
				} else {
					toast.open({
						type: 'danger',
						title: _format('error_global', {
							default: 'Failed to update status',
						}),
					});
					throw new Error('Failed to update status');
				}
			} catch (error) {
				console.error(error);
			}
			devicesInProgress.created[channelID] = false;
		}

		/**
		 * Cleanup: Remove channel devices that no longer exist in status.sys.ch
		 * Example: If we had 3 components and now have 2, delete deviceId_2
		 */
		const expectedChannelCount = status.sys.ch.length;

		const existingChannelDevices = wallDisplayChannels.filter((id) => {
			// Only consider numeric channel devices (_1, _2, _3...), not _100 (media player) or _101 (thermostat)
			const channel = id.split('_')[1];
			return channel.match(/\d{1}/);
		});

		// Delete channel devices that exceed the current component count
		for (const channelDeviceId of existingChannelDevices) {
			const match = channelDeviceId.match(/_(\d{1})$/);
			if (match) {
				const channelIndex = parseInt(match[1]);
				// If this channel index is >= the number of components in status.sys.ch, delete it
				if (channelIndex >= expectedChannelCount) {
					try {
						const res = await postReq(`/interface/device/bulk_delete`, {
							ids: JSON.stringify([channelDeviceId]),
						});

						// Remove from store immediately for reactive UI
						if (res?.isok) {
							devices.remove(channelDeviceId);
						} else {
							throw new Error('Failed to delete channel device');
						}
					} catch (error) {
						console.error(error);
					}
				}
			}
		}
	} else if (Array.isArray(status.sys.ch) && status.sys.ch.length === 1) {
		/**
		 * Special case: If only 1 component remains in status.sys.ch,
		 * remove ALL numeric channel devices (keep only the main device)
		 * and update the main device's category/mode if needed
		 */

		// Update main device if its component type changed
		const component = status.sys.ch[0]; // e.g., 'switch:0', 'light:0'
		const componentType = component.split(':')[0]; // Extract type: 'switch' or 'light'

		// Map component type to device category and mode
		// Currently, 'switch' maps to relay, all others are mapped to their own category.
		const newCategory = componentType === 'switch' ? 'relay' : componentType;

		// Update main device if category or mode has changed
		if (realDevice.category !== newCategory || realDevice.mode !== newCategory) {
			const updatedDevice = {
				...realDevice,
				category: newCategory,
				mode: newCategory,
			};
			try {
				const res = await postReq(`/interface/device/save`, {
					id: realID,
					data: JSON.stringify(updatedDevice),
				});

				// Update store immediately for reactive UI
				if (res?.isok) {
					devices.update(updatedDevice);
				} else {
					throw new Error('Failed to update main device');
				}
			} catch (error) {
				console.error(error);
			}
		}

		// Remove all numeric channel devices (deviceId_1, deviceId_2, etc.)
		const numericChannelDevices = wallDisplayChannels.filter((id) => {
			const match = id.match(/_(\d+)$/);
			if (match) {
				const channelIndex = parseInt(match[1]);
				return channelIndex < 100; // Only numeric channels, not _100 or _101
			}
			return false;
		});

		if (numericChannelDevices.length > 0) {
			try {
				const res = await postReq(`/interface/device/bulk_delete`, {
					ids: JSON.stringify(numericChannelDevices),
				});

				// Remove from store immediately for reactive UI
				if (res?.isok) {
					numericChannelDevices.forEach((deviceId) => {
						devices.remove(deviceId);
					});
				} else {
					throw new Error('Failed to delete channel devices');
				}
			} catch (error) {
				console.error(error);
			}
		}
	}
};

/**
 * Updates all channel devices for a wall display based on status and settings
 *
 * This is the main orchestrator function that:
 * - Creates/updates/deletes channel devices based on status.sys.ch components
 * - Updates the main device category/mode when component types change
 * - Removes thermostat devices when thermostat is disabled
 * - Synchronizes status and settings across all channel devices
 *
 * Requires firmware version >= 2.3.0 for separate component support.
 *
 * @param {string} id - The wall display device ID
 * @param {object | null} settingsParam - Optional device settings (uses store if not provided)
 * @param {object | null} statusParam - Optional device status (uses store if not provided)
 * @returns {Promise<void>}
 *
 * @example
 * // Update channels after status change
 * await updateWallDisplayChannels('shellywd-abc123', null, newStatus);
 *
 * @example
 * // Update channels using current store values
 * await updateWallDisplayChannels('shellywd-abc123');
 */
export const updateWallDisplayChannels = async (id, settingsParam = null, statusParam = null) => {
	const realID = real_id(id);
	const realDevice = get(devices)[realID];
	const status = statusParam || devices_status.getByRealId(realID);
	const settings = settingsParam || devices_settings.getByRealId(realID);

	if (!isWallDisplaySeparateComponentsSupported(settings)) {
		return;
	}

	const wallDisplayChannels = Object.keys(get(devices)).filter((id) => {
		return isWallDisplay(get(devices)?.[id]?.type) && id?.includes(realID + '_');
	});
	const hasThermostat = settings?.['thermostat:0'] !== undefined;

	// Handle channel device creation/deletion based on status.sys.ch
	await updateChannelDevicesFromComponents(realID, realDevice, status, settings, wallDisplayChannels);

	// Remove thermostat device if thermostat is no longer enabled
	if (
		!hasThermostat &&
		wallDisplayChannels.includes(realID + '_101') &&
		Object.keys(get(devices)).includes(realID + '_101')
	) {
		try {
			const res: CloudApiResponse = await postReq(`/interface/device/bulk_delete`, {
				ids: JSON.stringify([realID + '_101']),
			});
			if (res?.isok) {
				devices.remove(realID + '_101');
			} else {
				throw new Error('Failed to delete thermostat device');
			}
		} catch (error) {
			console.error(error);
		}
	}

	// Channel devices share the parent real_id status/settings record.
	// Do not write filtered per-channel status — that overwrites switch:0 (FE-2385).
	if (wallDisplayChannels.length > 0) {
		devices.update(realDevice);
	}
};

/**
 * Checks if the wall display firmware supports separate component functionality
 *
 * Wall displays with firmware version 2.3.0 and above support automatic splitting
 * of multiple components (switch:0, switch:1, light:0, etc.) into separate channel devices.
 *
 * @param {object} settings - Device settings object containing DeviceInfo.ver
 * @returns {boolean} True if firmware version is >= 2.3.0, false otherwise
 *
 * @example
 * const settings = { DeviceInfo: { ver: '2.3.0-beta1' } };
 * isWallDisplaySeparateComponentsSupported(settings); // returns true
 *
 * @example
 * const settings = { DeviceInfo: { ver: '2.2.9' } };
 * isWallDisplaySeparateComponentsSupported(settings); // returns false
 */
export const isWallDisplaySeparateComponentsSupported = (settings) => {
	try {
		const version = settings?.DeviceInfo?.ver.match(/(\d+\.\d+\.\d+)/)?.[0];

		if (version) {
			return compare(version, '2.3.0', '>=');
		}
	} catch (error) {
		console.warn(error.message);
		return false;
	}
};
